/**
 * FreshnessChip - Sprint 2, F2.6
 * ===============================
 *
 * Small chip rendered next to report titles ("Last calculated at 14:23
 * today" / "Yesterday at 09:00") so users always know how fresh the
 * numbers are. Builds trust on every report view.
 *
 * Usage:
 *   <FreshnessChip timestamp={report.generated_at} onRefresh={() => refresh()} />
 *
 * Props:
 *   timestamp - ISO date string or null. If null, shows "Calculating…"
 *   onRefresh - optional, renders a refresh button next to the chip
 *   testId    - optional override for the test-id
 */
import React from 'react';
import { RefreshCw, Clock } from 'lucide-react';

function _format(ts) {
  if (!ts) return null;
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Last calculated at ${time} today`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `Last calculated yesterday at ${time}`;
  }
  const dateStr = d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  return `Last calculated ${dateStr} at ${time}`;
}

const FreshnessChip = ({ timestamp, onRefresh, refreshing = false, className = '', testId = 'freshness-chip' }) => {
  const label = _format(timestamp);

  return (
    <div
      className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground ${className}`}
      data-testid={testId}
    >
      <Clock className="w-3.5 h-3.5" aria-hidden="true" />
      <span data-testid={`${testId}-label`}>
        {label || 'Calculating…'}
      </span>
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          data-testid={`${testId}-refresh`}
          className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded hover:bg-muted disabled:opacity-50 transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw
            className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
};

export default FreshnessChip;
export { FreshnessChip };
